import React from "react";
import { cx } from "@/lib/format";

export function PageHeader({ eyebrow, title, description, actions, testid }) {
  return (
    <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4 mb-8 md:mb-10" data-testid={testid}>
      <div>
        {eyebrow && <div className="overline mb-2">{eyebrow}</div>}
        <h1 className="font-display text-3xl md:text-4xl lg:text-5xl leading-[1.05] tracking-tight">
          {title}
        </h1>
        {description && (
          <p className="text-white/55 text-[13px] md:text-sm mt-3 max-w-2xl">{description}</p>
        )}
      </div>
      {actions && <div className="flex flex-wrap gap-2">{actions}</div>}
    </div>
  );
}

export function Card({ children, className, as: Tag = "div", hover = true, ...rest }) {
  return (
    <Tag
      {...rest}
      className={cx("clara-card p-5 md:p-6", !hover && "hover:border-white/[0.08]", className)}
    >
      {children}
    </Tag>
  );
}

export function KPICard({ label, value, sub, accent, testid }) {
  return (
    <Card testid={testid} data-testid={testid} className="relative overflow-hidden">
      <div className="overline mb-3">{label}</div>
      <div className="kpi-value text-3xl md:text-4xl mb-2" style={{ color: accent || "#fff" }}>
        {value}
      </div>
      {sub && <div className="text-[12px] text-white/40 font-mono">{sub}</div>}
      <div className="absolute top-0 left-0 w-full h-[2px]" style={{ background: "linear-gradient(90deg, #C5A059 0%, transparent 60%)" }} />
    </Card>
  );
}

export function Button({
  children,
  variant = "primary",
  size = "md",
  className,
  ...rest
}) {
  const variants = {
    primary: "bg-[#C5A059] text-black hover:bg-[#D4AF37] border border-transparent",
    ghost: "bg-transparent text-white/70 hover:text-white hover:bg-white/[0.04] border border-transparent",
    outline: "bg-transparent text-white border border-white/15 hover:border-[#C5A059] hover:text-[#D4AF37]",
    danger: "bg-red-600/90 text-white hover:bg-red-500 border border-transparent",
    navy: "bg-[#152A55] text-white hover:bg-[#1e3a72] border border-transparent",
  };
  const sizes = {
    sm: "px-3 py-1.5 text-[12px] rounded-md",
    md: "px-4 py-2 text-[13px] rounded-md",
    lg: "px-5 py-2.5 text-sm rounded-md",
  };
  return (
    <button
      {...rest}
      className={cx(
        "inline-flex items-center gap-2 font-medium transition-all disabled:opacity-50 disabled:cursor-not-allowed",
        variants[variant],
        sizes[size],
        className
      )}
    >
      {children}
    </button>
  );
}

export function Badge({ children, tone = "neutral", className, ...rest }) {
  const tones = {
    neutral: { bg: "rgba(255,255,255,0.04)", text: "#a1a1aa", border: "rgba(255,255,255,0.1)" },
    gold: { bg: "rgba(197,160,89,0.12)", text: "#D4AF37", border: "rgba(197,160,89,0.3)" },
    navy: { bg: "rgba(21,42,85,0.3)", text: "#93c5fd", border: "rgba(21,42,85,0.6)" },
    green: { bg: "rgba(16,185,129,0.1)", text: "#34d399", border: "rgba(16,185,129,0.3)" },
    red: { bg: "rgba(239,68,68,0.1)", text: "#f87171", border: "rgba(239,68,68,0.3)" },
    yellow:{ bg: "rgba(234,179,8,0.1)", text: "#facc15", border: "rgba(234,179,8,0.3)" },
  };
  const t = tones[tone] || tones.neutral;
  return (
    <span
      {...rest}
      className={cx("inline-flex items-center gap-1 px-2 py-0.5 text-[10px] font-medium tracking-wide uppercase rounded", className)}
      style={{ background: t.bg, color: t.text, border: `1px solid ${t.border}` }}
    >
      {children}
    </span>
  );
}

export function Input({ className, ...rest }) {
  return (
    <input
      {...rest}
      className={cx(
        "w-full px-3 py-2 text-sm bg-white/[0.03] border border-white/10 rounded-md text-white placeholder:text-white/30 focus:outline-none focus:border-[#C5A059]/50 transition-colors",
        className
      )}
    />
  );
}

export function Textarea({ className, ...rest }) {
  return (
    <textarea
      {...rest}
      className={cx(
        "w-full px-3 py-2 text-sm bg-white/[0.03] border border-white/10 rounded-md text-white placeholder:text-white/30 focus:outline-none focus:border-[#C5A059]/50 transition-colors resize-none",
        className
      )}
    />
  );
}

export function Select({ children, className, ...rest }) {
  return (
    <select
      {...rest}
      className={cx(
        "w-full px-3 py-2 text-sm bg-white/[0.03] border border-white/10 rounded-md text-white focus:outline-none focus:border-[#C5A059]/50 transition-colors",
        className
      )}
    >
      {children}
    </select>
  );
}

export function Spinner({ size = 16 }) {
  return (
    <span
      className="inline-block border-2 border-current border-t-transparent rounded-full animate-spin"
      style={{ width: size, height: size }}
    />
  );
}

export function EmptyState({ icon: Icon, title, description, action }) {
  return (
    <div className="flex flex-col items-center justify-center py-16 text-center">
      {Icon && <Icon size={32} className="text-white/20 mb-4" />}
      <div className="font-display text-xl mb-1">{title}</div>
      {description && <div className="text-white/40 text-sm max-w-sm mb-4">{description}</div>}
      {action}
    </div>
  );
}
